import { useUnreadAlerts, useMarkAlertRead, useMarkAllAlertsRead } from '@/hooks/useUserAlerts';
import { X, AlertTriangle, CheckCircle, Info, CreditCard, Clock, Gift, Calendar, CalendarCheck, CalendarX, Phone } from 'lucide-react';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

export function AlertNotifications() {
  const { data: alerts, isLoading } = useUnreadAlerts();
  const markRead = useMarkAlertRead();
  const markAllRead = useMarkAllAlertsRead();
  const [isOpen, setIsOpen] = useState(false);
  const [dismissedAlerts, setDismissedAlerts] = useState<string[]>([]);
  const navigate = useNavigate();

  // Auto-open if there are critical alerts
  useEffect(() => {
    if (alerts?.some(a => a.alert_type === 'delinquent' || a.alert_type === 'credits_expired')) {
      setIsOpen(true);
    }
  }, [alerts]);

  // Handle clicks on the sidebar notification bell
  useEffect(() => {
    const handleBellClick = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(!isOpen);
    };

    const bellButton = document.getElementById('sidebar-notification-bell');
    if (bellButton) {
      bellButton.addEventListener('click', handleBellClick);
      return () => bellButton.removeEventListener('click', handleBellClick);
    }
  }, [isOpen]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      const panel = document.querySelector('[data-notification-panel]');
      const bell = document.getElementById('sidebar-notification-bell');
      
      if (isOpen && panel && !panel.contains(target) && bell && !bell.contains(target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'low_credits':
        return <CreditCard className="h-4 w-4 text-yellow-600" />;
      case 'delinquent':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'restored':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'credits_expired':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'credits_expiring_soon':
        return <Clock className="h-4 w-4 text-orange-600" />;
      case 'free_trial_started':
        return <Gift className="h-4 w-4 text-blue-600" />;
      case 'appointment_scheduled':
        return <Calendar className="h-4 w-4 text-green-600" />;
      case 'appointment_rescheduled':
        return <Calendar className="h-4 w-4 text-blue-600" />;
      case 'appointment_confirmed':
        return <CalendarCheck className="h-4 w-4 text-green-600" />;
      case 'appointment_cancelled':
        return <CalendarX className="h-4 w-4 text-red-600" />;
      case 'call_requires_attention':
        return <Phone className="h-4 w-4 text-orange-600" />;
      default:
        return <Info className="h-4 w-4 text-blue-600" />;
    }
  };

  const getAlertStyle = (type: string) => {
    switch (type) {
      case 'low_credits':
        return 'border-l-4 border-l-yellow-400 bg-yellow-50/80 hover:bg-yellow-50';
      case 'delinquent':
        return 'border-l-4 border-l-red-400 bg-red-50/80 hover:bg-red-50';
      case 'restored':
        return 'border-l-4 border-l-green-400 bg-green-50/80 hover:bg-green-50';
      case 'credits_expired':
        return 'border-l-4 border-l-red-400 bg-red-50/80 hover:bg-red-50';
      case 'credits_expiring_soon':
        return 'border-l-4 border-l-orange-400 bg-orange-50/80 hover:bg-orange-50';
      case 'free_trial_started':
        return 'border-l-4 border-l-blue-400 bg-blue-50/80 hover:bg-blue-50';
      case 'appointment_scheduled':
        return 'border-l-4 border-l-green-400 bg-green-50/80 hover:bg-green-50';
      case 'appointment_rescheduled':
        return 'border-l-4 border-l-blue-400 bg-blue-50/80 hover:bg-blue-50';
      case 'appointment_confirmed':
        return 'border-l-4 border-l-green-400 bg-green-50/80 hover:bg-green-50';
      case 'appointment_cancelled':
        return 'border-l-4 border-l-red-400 bg-red-50/80 hover:bg-red-50';
      case 'call_requires_attention':
        return 'border-l-4 border-l-orange-400 bg-orange-50/80 hover:bg-orange-50';
      default:
        return 'border-l-4 border-l-blue-400 bg-blue-50/80 hover:bg-blue-50';
    }
  };

  const handleDismiss = (alertId: string) => {
    setDismissedAlerts([...dismissedAlerts, alertId]);
    markRead.mutate(alertId);
  };

  const handleAlertClick = (alert: any) => {
    // If it's a call_requires_attention alert with a call_log_id, navigate to calls page
    if (alert.alert_type === 'call_requires_attention' && alert.call_log_id) {
      navigate(`/dashboard/calls?highlight=${alert.call_log_id}`);
      setIsOpen(false); // Close the notifications panel
      // Don't dismiss the alert - keep it visible for reference
    } else {
      // Default behavior for other alerts
      handleDismiss(alert.id);
    }
  };

  const visibleAlerts = alerts?.filter(a => !dismissedAlerts.includes(a.id)) || [];
  const unreadCount = visibleAlerts.length;

  // Determine which alerts should be shown as floating notifications (high priority)
  const criticalAlerts = visibleAlerts.filter(a => 
    a.alert_type === 'delinquent' || 
    a.alert_type === 'credits_expired' || 
    a.alert_type === 'credits_expiring_soon' ||
    a.alert_type === 'low_credits'
  );

  const getPriorityOrder = (alertType: string) => {
    switch (alertType) {
      case 'delinquent':
      case 'credits_expired':
        return 1;
      case 'call_requires_attention':
        return 2;
      case 'credits_expiring_soon':
        return 3;
      case 'low_credits':
        return 4;
      case 'appointment_scheduled':
      case 'appointment_confirmed':
        return 5;
      case 'appointment_rescheduled':
        return 6;
      case 'appointment_cancelled':
        return 7;
      default:
        return 8;
    }
  };

  // Sort alerts by priority for display
  const sortedCriticalAlerts = criticalAlerts
    .sort((a, b) => getPriorityOrder(a.alert_type) - getPriorityOrder(b.alert_type))
    .slice(0, 2); // Show max 2 floating notifications

  if (isLoading) return null;

  return (
    <>
      {/* Slide-out Panel */}
      {isOpen && (
        <div 
          className="fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 border-l"
          data-notification-panel
        >
          <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                <p className="text-sm text-gray-600">
                  {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                </p>
              </div>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setIsOpen(false)}
                className="hover:bg-white/50"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            {unreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => markAllRead.mutate()}
                className="mt-3 text-xs"
                disabled={markAllRead.isPending}
              >
                Mark all as read
              </Button>
            )}
          </div>
          
          <div className="overflow-y-auto h-full pb-20">
            {visibleAlerts.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Info className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p>No new notifications</p>
              </div>
            ) : (
              <div className="space-y-1">
                {visibleAlerts.map((alert) => (
                  <div
                    key={alert.id}
                    className={cn(
                      "p-4 m-3 rounded-lg border cursor-pointer transition-colors",
                      getAlertStyle(alert.alert_type),
                      // Add visual indicator for clickable call attention alerts
                      alert.alert_type === 'call_requires_attention' && alert.call_log_id 
                        ? "hover:shadow-md ring-1 ring-orange-200 hover:ring-orange-300" 
                        : ""
                    )}
                    onClick={() => handleAlertClick(alert)}
                  >
                    <div className="flex items-start gap-3">
                      {getAlertIcon(alert.alert_type)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 leading-tight">
                          {alert.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 hover:bg-background/50 opacity-60 hover:opacity-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDismiss(alert.id);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Floating Notifications for Critical Alerts */}
      <div className="fixed bottom-6 right-6 z-40 space-y-3 max-w-sm">
        {sortedCriticalAlerts.map((alert) => (
          <div
            key={alert.id}
            className={cn(
              "p-4 rounded-xl shadow-lg border backdrop-blur-sm flex items-start gap-3 animate-in slide-in-from-bottom-2 duration-500 cursor-pointer transition-all",
              alert.alert_type === 'delinquent' || alert.alert_type === 'credits_expired'
                ? "bg-red-50/90 border-red-200 text-red-900"
                : alert.alert_type === 'credits_expiring_soon'
                ? "bg-orange-50/90 border-orange-200 text-orange-900"
                : "bg-yellow-50/90 border-yellow-200 text-yellow-900",
              // Add hover effect for clickable call attention alerts
              alert.alert_type === 'call_requires_attention' && alert.call_log_id 
                ? "hover:shadow-xl hover:scale-105" 
                : ""
            )}
            onClick={() => handleAlertClick(alert)}
          >
            {getAlertIcon(alert.alert_type)}
            <div className="flex-1">
              <p className="text-sm font-medium leading-tight">
                {alert.message}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 hover:bg-background/50"
              onClick={(e) => {
                e.stopPropagation();
                handleDismiss(alert.id);
              }}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>
    </>
  );
} 